import React, { useEffect, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from '@mui/material';
import { addNode, TutorStructureNode } from '../../services/tutorStructureService';
import { flattenStructureForPicker, HierarchyPickerOption } from '../../utils/topicHierarchyPicker';

const ROOT_OPTION: HierarchyPickerOption = { id: -1, label: 'No parent (root topic)', depth: 0 };

interface AddTopicDialogProps {
  open: boolean;
  onClose: () => void;
  courseId: number;
  structureNodes: TutorStructureNode[];
  initialParentId?: number | null;
  onCreated: (node: TutorStructureNode) => void;
}

const AddTopicDialog: React.FC<AddTopicDialogProps> = ({
  open,
  onClose,
  courseId,
  structureNodes,
  initialParentId,
  onCreated,
}) => {
  const [title, setTitle] = useState('');
  const [parentOption, setParentOption] = useState<HierarchyPickerOption>(ROOT_OPTION);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const parentOptions: HierarchyPickerOption[] = [ROOT_OPTION, ...flattenStructureForPicker(structureNodes)];

  useEffect(() => {
    if (!open) return;
    setTitle('');
    setError(null);
    const preselected = initialParentId != null
      ? parentOptions.find((o) => o.id === initialParentId)
      : undefined;
    setParentOption(preselected ?? ROOT_OPTION);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialParentId]);

  const handleSubmit = async () => {
    const label = title.trim();
    if (!label) {
      setError('Enter a topic title.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const parentId = parentOption.id === ROOT_OPTION.id ? null : parentOption.id;
      const created = await addNode(courseId, { nodeType: 'CUSTOM_TOPIC', label, parentId });
      onCreated(created);
      onClose();
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to add topic. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Add Topic</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
        {error && <Alert severity="error">{error}</Alert>}
        <TextField
          autoFocus
          label="Topic title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') handleSubmit(); }}
          fullWidth
        />
        <Autocomplete
          options={parentOptions}
          value={parentOption}
          onChange={(_event, newValue) => setParentOption(newValue ?? ROOT_OPTION)}
          getOptionLabel={(option) => `${'—'.repeat(option.depth)}${option.depth > 0 ? ' ' : ''}${option.label}`}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          disableClearable
          renderInput={(params) => <TextField {...params} label="Parent" />}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>Cancel</Button>
        <Button variant="contained" onClick={handleSubmit} disabled={submitting}>
          {submitting ? 'Adding…' : 'Add Topic'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddTopicDialog;
